/**
 * Resolve People API Contract (SCH-5038)
 *
 * POST /internal/artifacts/people/resolve
 * Gated by ARTIFACT_PEOPLE_LOOKUP_KEY only (never INTERNAL_API_KEY). The ops store calls it when an agent shares an AI
 * artifact (`POST /artifacts/share`): each email either names a School Journey account (its `User.publicId`, the
 * platform user id a grant matches on, and its name) or nothing. Exact match, case-insensitive; an account whose email
 * is the placeholder or empty never matches. Read-only: nobody is invited and no account is created.
 *
 * Source of truth: `apps/backend/src/api/_internal/artifacts/use-cases/resolve-people/schema.ts`
 * Consumer: `apps/ops-store/convex/aiArtifactsShareHttp.ts` (the store does not depend on shared-types; it mirrors this
 * answer by hand)
 */

import { z } from 'zod'

/** How many emails one call resolves at most. */
export const RESOLVE_PEOPLE_MAX_EMAILS = 50

/** The emails, trimmed and lower-cased; repeats are answered once. */
export const ResolvePeopleRequestSchema = z.strictObject({
  emails: z.array(z.string().trim().toLowerCase().email()).min(1).max(RESOLVE_PEOPLE_MAX_EMAILS)
})

export type ResolvePeopleRequest = z.infer<typeof ResolvePeopleRequestSchema>

/** One email's answer: its account, or `platformUserId: null` when no account has exactly that email. */
export const ResolvedPersonSchema = z.union([
  z.object({ email: z.string().min(1), platformUserId: z.string().min(1), name: z.string() }),
  z.object({ email: z.string().min(1), platformUserId: z.null() })
])

export type ResolvedPerson = z.infer<typeof ResolvedPersonSchema>

/** One entry per distinct requested email, in the order first asked. */
export const ResolvePeopleResponseSchema = z.object({
  data: z.array(ResolvedPersonSchema).max(RESOLVE_PEOPLE_MAX_EMAILS)
})

export type ResolvePeopleResponse = z.infer<typeof ResolvePeopleResponseSchema>
